import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { listPushApps, setAppPush, testAppPush, generateTestLink, listTestDevices, type PushApp, type PushTestDevice } from '@/api/push';
import { listAccounts, type FirebaseAccount } from '@/api/firebase';

export function PushApps() {
  const queryClient = useQueryClient();

  const { data: appsData, isLoading: appsLoading } = useQuery({
    queryKey: ['push-apps'],
    queryFn: listPushApps,
  });
  const apps = appsData?.apps ?? [];

  const { data: accounts = [] } = useQuery<FirebaseAccount[]>({
    queryKey: ['firebase-accounts'],
    queryFn: listAccounts,
  });

  const inval = () => queryClient.invalidateQueries({ queryKey: ['push-apps'] });

  // ─── Edit modal ─────────────────────────────────────────────
  const [editApp, setEditApp] = useState<PushApp | null>(null);
  const [editAccountId, setEditAccountId] = useState<string>('');
  const [editProjectId, setEditProjectId] = useState('');
  const [editError, setEditError] = useState('');

  const openEdit = (app: PushApp) => {
    setEditApp(app);
    setEditAccountId(app.push_firebase_account_id != null ? String(app.push_firebase_account_id) : '');
    setEditProjectId(app.push_firebase_project_id ?? '');
    setEditError('');
  };

  const saveMut = useMutation({
    mutationFn: ({ id, accountId, projectId }: { id: string; accountId: string; projectId: string }) =>
      setAppPush(id, {
        firebase_account_id: accountId ? Number(accountId) : null,
        firebase_project_id: projectId.trim() || null,
      }),
    onSuccess: () => { setEditApp(null); inval(); },
    onError: (err: Error) => setEditError(err.message),
  });

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editApp) return;
    setEditError('');
    saveMut.mutate({ id: editApp.id, accountId: editAccountId, projectId: editProjectId });
  };

  // ─── Test modal ──────────────────────────────────────────────
  const [testApp, setTestApp] = useState<PushApp | null>(null);
  const [deviceId, setDeviceId] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState('');

  const openTest = (app: PushApp) => {
    setTestApp(app);
    setDeviceId('');
    setTestResult(null);
    setTestError('');
  };

  const testMut = useMutation({
    mutationFn: ({ id, did }: { id: string; did: string }) => testAppPush(id, did),
    onSuccess: (data) => setTestResult(JSON.stringify(data, null, 2)),
    onError: (err: Error) => setTestError(err.message),
  });

  const submitTest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!testApp) return;
    const did = deviceId.trim();
    if (!did) { setTestError('Device ID or FCM token is required'); return; }
    setTestError('');
    setTestResult(null);
    testMut.mutate({ id: testApp.id, did });
  };

  // ─── Test link modal ─────────────────────────────────────────
  const [linkApp, setLinkApp] = useState<PushApp | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [linkError, setLinkError] = useState('');

  const openLink = async (app: PushApp) => {
    setLinkApp(app); setLinkUrl(null); setLinkError('');
    try {
      const res = await generateTestLink(app.id);
      setLinkUrl(res.url);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : String(err));
    }
  };

  const { data: testDevicesData } = useQuery({
    queryKey: ['push-test-devices', linkApp?.id],
    queryFn: () => listTestDevices(linkApp!.id),
    enabled: !!linkApp,
  });
  const testDevices: PushTestDevice[] = testDevicesData?.devices ?? [];

  // ─── Render ──────────────────────────────────────────────────
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-content">Push · Apps</h1>
        <p className="text-sm text-content-subtle mt-1">
          Configure FCM credentials per app to enable push notifications.
        </p>
      </div>

      {appsLoading ? (
        <div className="text-sm text-content-subtle">Loading…</div>
      ) : (
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-xs text-content-subtle uppercase">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Platform</th>
                <th className="text-left px-4 py-3">Bundle ID</th>
                <th className="text-left px-4 py-3">FCM Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {apps.map((app) => (
                <tr key={app.id}>
                  <td className="px-4 py-3 font-medium text-content">{app.name}</td>
                  <td className="px-4 py-3 text-content-muted">{app.platform}</td>
                  <td className="px-4 py-3 font-mono text-xs text-content-muted">{app.bundle_id}</td>
                  <td className="px-4 py-3">
                    {app.push_firebase_account_id ? (
                      <span className="text-success text-xs">
                        configured ({app.firebase_account_name ?? String(app.push_firebase_account_id)})
                      </span>
                    ) : (
                      <span className="text-content-subtle text-xs">not configured</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                    <EditOnly>
                      <button onClick={() => openEdit(app)} className="text-xs text-primary hover:underline">Edit</button>
                      <button onClick={() => openTest(app)} className="text-xs text-content-subtle hover:text-content">Test</button>
                    </EditOnly>
                    <button onClick={() => openLink(app)} className="text-xs text-content-subtle hover:text-content">Test link</button>
                  </td>
                </tr>
              ))}
              {apps.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-content-subtle text-sm" colSpan={5}>
                    No apps found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={!!editApp} onClose={() => setEditApp(null)} title="Configure FCM for app">
        <form onSubmit={submitEdit} className="space-y-4">
          {editError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{editError}</div>
          )}
          <div>
            <p className="text-sm text-content-subtle mb-3">
              App: <span className="font-medium text-content">{editApp?.name}</span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Firebase Account</label>
            <Select
              value={editAccountId}
              onChange={setEditAccountId}
              placeholder="— none —"
              options={[
                { value: '', label: '— none —' },
                ...accounts.map((a) => ({ value: String(a.id), label: a.name })),
              ]}
              searchable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">FCM Project ID</label>
            <input
              type="text"
              value={editProjectId}
              onChange={(e) => setEditProjectId(e.target.value)}
              placeholder="e.g. my-firebase-project"
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
            <p className="mt-1 text-xs text-content-subtle">The Firebase project ID used for FCM v1 API calls.</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setEditApp(null)}>Cancel</Button>
            <Button type="submit" loading={saveMut.isPending}>Save</Button>
          </div>
        </form>
      </Modal>

      {/* Test Modal */}
      <Modal isOpen={!!testApp} onClose={() => setTestApp(null)} title="Test push notification">
        <form onSubmit={submitTest} className="space-y-4">
          {testError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{testError}</div>
          )}
          <div>
            <p className="text-sm text-content-subtle mb-3">
              App: <span className="font-medium text-content">{testApp?.name}</span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Device ID or FCM token</label>
            <input
              type="text"
              autoFocus
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="FCM registration token or device UUID"
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setTestApp(null)}>Cancel</Button>
            <Button type="submit" loading={testMut.isPending}>Send test</Button>
          </div>
          {testResult && (
            <div>
              <p className="text-xs font-medium text-content-muted mb-1">Result:</p>
              <pre className="bg-surface-muted border border-border rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all">{testResult}</pre>
            </div>
          )}
        </form>
      </Modal>

      {/* Test link Modal */}
      <Modal isOpen={!!linkApp} onClose={() => setLinkApp(null)} title="Browser push test link">
        <div className="space-y-4">
          {linkError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{linkError}</div>
          )}
          {!linkApp?.push_firebase_account_id && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-xs text-danger">
              This app has no Firebase account configured. Configure it (with web push credentials) first.
            </div>
          )}
          <p className="text-sm text-content-subtle">
            Open this link in any browser, allow notifications, then send a test push below or run a schedule's "Test run".
          </p>
          {linkUrl && (
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Public link</label>
              <div className="flex gap-2">
                <input readOnly value={linkUrl}
                  className="block w-full px-3 py-2 border border-border-strong rounded-md text-xs font-mono bg-surface-muted" />
                <Button type="button" variant="secondary"
                  onClick={() => navigator.clipboard.writeText(linkUrl)}>Copy</Button>
              </div>
              <p className="mt-1 text-xs text-content-subtle">
                Open this on any device/browser. The page is served by the API origin (not the dashboard).
              </p>
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-content-muted mb-2">Registered browser test devices</p>
            {testDevices.length === 0 ? (
              <p className="text-xs text-content-subtle">None yet. Open the link and allow notifications.</p>
            ) : (
              <table className="min-w-full text-xs">
                <tbody className="divide-y divide-border">
                  {testDevices.map((d) => (
                    <tr key={d.device_id}>
                      <td className="py-2 font-mono">{d.device_id}</td>
                      <td className="py-2 text-content-muted">{d.locale ?? '—'}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => { if (linkApp) testMut.mutate({ id: linkApp.id, did: d.device_id }); }}
                          className="text-primary hover:underline">Send test push</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {testResult && (
              <pre className="mt-2 bg-surface-muted border border-border rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all">{testResult}</pre>
            )}
          </div>
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={() => setLinkApp(null)}>Close</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
